import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  gatorProtectActions,
  pickGatorProtectSiteWithoutProtectionData,
  portalModalsActions,
} from '@/redux/modules';
import { MONETIZACAO_ENABLE_THREATS_DETECTED_MODAL } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type {
  DetectedThreadsPayload,
  GatorProtectGlobalsDependencies,
  GatorProtectPortalModalsRootState,
  GatorProtectPortalModalsSlice,
} from './GatorProtectGlobals.types';

let gatorProtectPlansRequestDispatched = false;

const FALLBACK_PORTAL_MODALS: GatorProtectPortalModalsSlice = { modals: [] };

/**
 * Header-scoped Gator Protect side effects: portal modals fetch on the home route,
 * parsed "site without protection" payload from Redux, one-shot plans list request,
 * and visibility rules for global modals (GrowthBook + feature flags).
 *
 * When the user is not on home or `gator_protect` is off, dispatches `portalModalsActions.clear()`
 * and resets local modal-dismiss state so a later home visit can show the threat modal again.
 *
 * @param summary - Redux `summary` slice; reads `features.gator_protect`.
 * @param isHome - From `useIsHomeRoute`; gates modals fetch and threat modal visibility.
 * @returns Grouped `data`, `utils`, and `gatorProtectModals` for `GatorProtectGlobals`.
 */
export function useGatorProtectGlobal(
  summary: GatorProtectGlobalsDependencies['summary'],
  isHome: boolean,
) {
  const dispatch = useDispatch();
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;
  const threatModalFeatureOn = useFeatureIsOn(MONETIZACAO_ENABLE_THREATS_DETECTED_MODAL);
  console.log('threatModalFeatureOn', threatModalFeatureOn);
  const portalModalsState = useSelector((state: GatorProtectPortalModalsRootState) => state.portalModals);
  const apiModals = portalModalsState ?? FALLBACK_PORTAL_MODALS;

  const [isDetectedThreadsVisible, setIsDetectedThreadsVisible] = useState(true);

  const detectedThreads = useMemo((): DetectedThreadsPayload | null => {
    const data = pickGatorProtectSiteWithoutProtectionData({
      modals: apiModals.modals,
    });
    const hostingId = data?.hosting_id;
    if (data == null || hostingId == null || Number.isNaN(Number(hostingId))) {
      return null;
    }
    return {
      threatCount: Math.max(0, Number(data.threats) || 0),
      hostingId: Number(hostingId),
    };
  }, [apiModals.modals]);

  const resetGlobals = useCallback(() => {
    dispatch(portalModalsActions.clear());
    setIsDetectedThreadsVisible(true);
  }, [dispatch]);

  const shouldFetchModals = useCallback(() => {
    if (!isHome || !gatorProtectEnabled) {
      resetGlobals();
      return false;
    }
    return true;
  }, [isHome, gatorProtectEnabled, resetGlobals]);

  const showDetectedThreads = isHome
    && gatorProtectEnabled
    && threatModalFeatureOn
    && detectedThreads !== null
    && isDetectedThreadsVisible;

  const fetchModalsWhenHome = useCallback(() => {
    if (shouldFetchModals()) {
      dispatch(portalModalsActions.fetch.request());
    }
  }, [dispatch, shouldFetchModals]);

  const getGatorProtectPlans = useCallback(() => {
    if (gatorProtectPlansRequestDispatched) {
      return;
    }
    gatorProtectPlansRequestDispatched = true;
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  return {
    data: {
      detectedThreads,
    },
    utils: {
      fetchModalsWhenHome,
      getGatorProtectPlans,
      hideDetectedThreadsModal: () => setIsDetectedThreadsVisible(false),
    },
    gatorProtectModals: {
      showAcknowledgment: false, // TODO: Implement acknowledgment modal logic
      showDetectedThreads,
    },
  };
}
